import { Game, supabase, TABLES, UserGame } from '@/lib/supabase';

export interface UserGameWithGame extends UserGame {
  games?: Game;
}

export interface GetUserGamesResult {
  success: boolean;
  userGames?: UserGameWithGame[];
  error?: string;
}

export interface GetGamesResult {
  success: boolean;
  games?: Game[];
  error?: string;
}

export interface AddUserGameResult {
  success: boolean;
  userGame?: UserGame;
  error?: string;
}

export interface UpdateUserGameResult {
  success: boolean;
  userGame?: UserGame;
  error?: string;
}

export interface DeleteUserGameResult {
  success: boolean;
  error?: string;
}

const USER_GAME_WITH_GAME_SELECT = `
  id,
  user_id,
  game_id,
  player_id,
  platform,
  created_at,
  updated_at,
  games:game_id (
    id,
    name,
    display_name,
    icon_name,
    color
  )
`;

export async function getGames(): Promise<GetGamesResult> {
  try {
    const { data, error } = await supabase
      .from(TABLES.GAMES)
      .select('*')
      .order('sort_order', { ascending: true })
      .order('display_name', { ascending: true });

    if (error) {
      console.error('Error fetching games:', error);
      return { success: false, error: error.message };
    }

    return { success: true, games: data || [] };
  } catch (error) {
    console.error('Unexpected error fetching games:', error);
    return { success: false, error: 'Failed to fetch games' };
  }
}

export async function getUserGames(userId: string): Promise<GetUserGamesResult> {
  try {
    const { data, error } = await supabase
      .from(TABLES.USER_GAMES)
      .select(USER_GAME_WITH_GAME_SELECT)
      .eq('user_id', userId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching user games:', error);
      return { success: false, error: error.message };
    }

    return { success: true, userGames: (data as unknown as UserGameWithGame[]) || [] };
  } catch (error) {
    console.error('Unexpected error fetching user games:', error);
    return { success: false, error: 'Failed to fetch user games' };
  }
}

export async function getUserGame(
  userId: string,
  gameId: string
): Promise<GetUserGamesResult> {
  try {
    const { data, error } = await supabase
      .from(TABLES.USER_GAMES)
      .select(USER_GAME_WITH_GAME_SELECT)
      .eq('user_id', userId)
      .eq('game_id', gameId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return { success: true, userGames: [] };
      }
      console.error('Error fetching user game:', error);
      return { success: false, error: error.message };
    }

    return { success: true, userGames: data ? [data as unknown as UserGameWithGame] : [] };
  } catch (error) {
    console.error('Unexpected error fetching user game:', error);
    return { success: false, error: 'Failed to fetch user game' };
  }
}

export async function addUserGame(
  userId: string,
  gameId: string,
  playerId?: string,
  platform?: UserGame['platform']
): Promise<AddUserGameResult> {
  try {
    const { data, error } = await supabase
      .from(TABLES.USER_GAMES)
      .insert({
        user_id: userId,
        game_id: gameId,
        player_id: playerId || null,
        platform: platform || null,
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        return { success: true };
      }
      console.error('Error adding user game:', error);
      return { success: false, error: error.message };
    }

    return { success: true, userGame: data };
  } catch (error) {
    console.error('Unexpected error adding user game:', error);
    return { success: false, error: 'Failed to add user game' };
  }
}

export async function updateUserGame(
  userGameId: string,
  userId: string,
  updates: { player_id?: string | null; platform?: UserGame['platform'] | null }
): Promise<UpdateUserGameResult> {
  try {
    const { error } = await supabase
      .from(TABLES.USER_GAMES)
      .update(updates)
      .eq('id', userGameId)
      .eq('user_id', userId);

    if (error) {
      console.error('Error updating user game:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Unexpected error updating user game:', error);
    return { success: false, error: 'Failed to update user game' };
  }
}

export async function deleteUserGame(userGameId: string): Promise<DeleteUserGameResult> {
  try {
    const { error } = await supabase
      .from(TABLES.USER_GAMES)
      .delete()
      .eq('id', userGameId);

    if (error) {
      console.error('Error deleting user game:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Unexpected error deleting user game:', error);
    return { success: false, error: 'Failed to delete user game' };
  }
}
